<script setup lang="ts">
import { computed } from 'vue'
import { Lock } from 'lucide-vue-next'
import { Link } from '@inertiajs/vue3'

interface Props {
  streamUrl?: string | null
  documentUrl?: string | null
  hasSubscription: boolean
  teacherSlug?: string | null
  tutorLinkPrefix?: string
}

const props = withDefaults(defineProps<Props>(), {
  streamUrl: null,
  documentUrl: null,
  teacherSlug: null,
  tutorLinkPrefix: '/tutors',
})

const viewPlansHref = computed(() => {
  if (props.teacherSlug) {
    return `${props.tutorLinkPrefix}/${props.teacherSlug}`
  }
  return '/tutors'
})

const hasAccess = computed(() => !!props.streamUrl || !!props.documentUrl)
const isDocument = computed(() => !!props.documentUrl && !props.streamUrl)
</script>

<template>
  <section id="video-player" class="rounded-xl overflow-hidden" :class="isDocument ? 'bg-gray-100' : 'bg-black'">
    <!-- Document Content -->
    <template v-if="isDocument && documentUrl">
      <div class="w-full h-[600px]">
        <iframe
          :src="documentUrl"
          class="w-full h-full"
          frameborder="0"
        />
      </div>
    </template>

    <!-- Video Content -->
    <template v-else-if="streamUrl">
      <div class="relative aspect-video">
        <iframe
          :src="streamUrl"
          class="w-full h-full"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
        />
      </div>
    </template>

    <!-- No Access - Paywall -->
    <template v-else>
      <div class="relative aspect-video">
        <div class="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-700">
          <div class="text-center">
            <button
              class="w-20 h-20 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all mb-4 mx-auto shadow-2xl"
            >
              <Lock class="text-brand-blue text-3xl ml-1" />
            </button>
            <h3 class="text-white text-xl font-bold mb-2">Subscribe to Access</h3>
            <p class="text-white text-sm opacity-80 mb-4">Unlock this content and thousands more</p>
            <Link
              :href="viewPlansHref"
              class="inline-block px-6 py-2.5 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              View Plans
            </Link>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>
